import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { servicesData } from "./ServicesData";
import {
  Phone,
  Mail,
  Send,
  X,
  ChevronRight,
  Scale,
  Shield,
  Award,
  MapPin,
} from "lucide-react";
import { Helmet } from "react-helmet-async";
import Header from "../../layout/Header";
import Footer from "../../layout/Footer";

/* ─── BR and TOKENS (extracted from R & P  logo) ─────────────────────── */
const C = {
  red: "#841619",
  redDark: "#5E0F11",
  redLight: "#A52025",
  cream: "#F2EBE1",
  creamDark: "#E3D8CA",
  gold: "#C89B3C",
  goldLight: "#E0B84A",
  textDark: "#1A1A1A",
  textMuted: "#6B5F55",
  white: "#FFFFFF",
};

/* ─── GOOGLE FONTS ────────────────────────────────────────────────── */
const FONTS = `
  @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,400;0,500;0,600;0,700;1,400;1,600&family=Nunito+Sans:wght@300;400;600;700&display=swap');
`;

const ServicesDetail = () => {
  const { serviceId } = useParams();
  const service = servicesData.find((s) => s.id === serviceId);

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    topic: service?.title || "",
    message: "",
  });
  const [success, setSuccess] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [focusedField, setFocusedField] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    setFormData((p) => ({ ...p, topic: service?.title || "" }));
  }, [service]);

  useEffect(() => {
    const esc = (e) => {
      if (e.key === "Escape") setSelectedItem(null);
    };
    window.addEventListener("keydown", esc);
    return () => window.removeEventListener("keydown", esc);
  }, []);

  if (!service)
    return (
      <div
        style={{ background: C.cream, color: C.textDark, minHeight: "60vh" }}
        className="flex flex-col items-center justify-center py-40 text-center"
      >
        <Scale size={52} color={C.gold} className="mb-6 opacity-40" />
        <p
          style={{
            fontFamily: "'Cormorant Garamond',serif",
            fontSize: "2.5rem",
            color: C.red,
            fontWeight: 700,
          }}
        >
          Service Not Found
        </p>
        <Link
          to="/services"
          style={{
            color: C.gold,
            fontSize: "0.7rem",
            letterSpacing: "3px",
            textTransform: "uppercase",
            marginTop: "1.5rem",
            borderBottom: `1px solid ${C.gold}`,
            paddingBottom: 2,
          }}
        >
          ← All Services
        </Link>
      </div>
    );

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "phone" && (value.length > 10 || /[^0-9]/.test(value))) return;
    setFormData((p) => ({ ...p, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || formData.phone.length !== 10) {
      alert("Please fill all fields correctly!");
      return;
    }
    const msg = `Hello! I am interested in ${formData.topic}\nName: ${formData.name}\nEmail: ${formData.email}\nPhone: ${formData.phone}\nMessage: ${formData.message}`;
    window.open(
      `https://wa.me/919661900233?text=${encodeURIComponent(msg)}`,
      "_blank",
    );
    setSuccess(true);
    setFormData({
      name: "",
      email: "",
      phone: "",
      topic: service.title,
      message: "",
    });
    setTimeout(() => setSuccess(false), 3000);
  };

  return (
    <>
      <Helmet>
        <title>{service.helmetTitle}</title>
        <meta name="description" content={service.helmetDescription} />
        <meta name="keywords" content={service.helmetKeywords} />

        <link
          rel="canonical"
          href={`https://rplawpartners.com/services/${service.slug}`}
        />
      </Helmet>

      <style>
        {FONTS}
        {`
        * { box-sizing: border-box; }
        .rp-body { font-family: 'Nunito Sans', sans-serif; background: ${C.cream}; color: ${C.textDark}; }
        .rp-display { font-family: 'Cormorant Garamond', serif; }

        /* HERO */
        .rp-hero { position: relative; height: 520px; display: flex; align-items: flex-end; overflow: hidden; }
        .rp-hero img.rp-hero-bg { position: absolute; inset: 0; width: 100%; height: 100%; object-fit: cover; filter: brightness(0.32) saturate(0.55); }
        .rp-hero-overlay {
          position: absolute; inset: 0;
          background: linear-gradient(160deg, ${C.redDark}cc 0%, transparent 55%, ${C.textDark}f0 100%);
        }
        .rp-hero-deco-left { position: absolute; left: 7%; top: 0; bottom: 0; width: 1px; opacity: 0.18;
          background: linear-gradient(to bottom, transparent, ${C.gold}, transparent); }
        .rp-hero-deco-right { position: absolute; right: 7%; top: 0; bottom: 0; width: 1px; opacity: 0.18;
          background: linear-gradient(to bottom, transparent, ${C.gold}, transparent); }
        .rp-hero-content { position: relative; z-index: 2; width: 100%; max-width: 1200px; margin: 0 auto; padding: 0 3rem 4rem; }
        .rp-hero-breadcrumb { display: flex; align-items: center; gap: 8px; font-size: 0.65rem; letter-spacing: 3px; text-transform: uppercase; color: rgba(255,255,255,0.5); margin-bottom: 1.2rem; }
        .rp-hero-breadcrumb a { color: ${C.gold}; text-decoration: none; transition: opacity .2s; }
        .rp-hero-breadcrumb a:hover { opacity: 0.7; }
        .rp-hero h1 { font-family:'Cormorant Garamond',serif; font-size: clamp(2.8rem, 5vw, 5rem); color: #fff; font-weight: 700; line-height: 1.05; letter-spacing: -0.02em; margin: 0 0 1.2rem; max-width: 700px; }
        .rp-hero-badge { display: inline-block; padding: 6px 20px; background: ${C.red}; color: #fff; font-size: 0.6rem; letter-spacing: 4px; text-transform: uppercase; font-weight: 700; }

        /* LAYOUT */
        .rp-main { max-width: 1200px; margin: 0 auto; padding: 5rem 3rem; display: grid; grid-template-columns: 1fr 380px; gap: 4rem; }
        @media(max-width: 1024px){ .rp-main { grid-template-columns: 1fr; padding: 3rem 1.5rem; } }

        /* SECTION HEADINGS */
        .rp-section-label { font-size: 0.6rem; letter-spacing: 4px; text-transform: uppercase; color: ${C.gold}; font-weight: 700; margin-bottom: 0.5rem; display: flex; align-items: center; gap: 10px; }
        .rp-section-label::before { content:''; display:inline-block; width:30px; height:1px; background:${C.gold}; }
        .rp-section-title { font-family:'Cormorant Garamond',serif; font-size: clamp(1.8rem,3vw,2.8rem); font-weight:700; color:${C.textDark}; margin:0 0 1.2rem; line-height:1.2; }
        .rp-intro-text { font-size:1.05rem; line-height:1.9; color:${C.textMuted}; margin-bottom:3rem; border-left:3px solid ${C.gold}; padding-left:1.5rem; background:${C.white}; padding: 1.5rem 1.5rem 1.5rem 2rem; }

        /* SECTION DIVIDER */
        .rp-divider { display:flex; align-items:center; gap:1rem; margin:3rem 0; opacity:.25; }
        .rp-divider::before, .rp-divider::after { content:''; flex:1; height:1px; background:${C.gold}; }

        /* SECTIONS */
        .rp-section { margin-bottom: 3.5rem; }
        .rp-section-content { font-size:1rem; line-height:1.85; color:${C.textMuted}; }
        .rp-list-grid { display:grid; grid-template-columns:1fr 1fr; gap:1rem; margin-top:1rem; }
        @media(max-width:600px){ .rp-list-grid { grid-template-columns:1fr; } }
        .rp-list-item {
          background: ${C.white}; padding: 1.1rem 1.2rem 1.1rem 1.5rem;
          border-left: 3px solid transparent; border-top: 1px solid rgba(0,0,0,0.06);
          font-size: 0.9rem; color: ${C.textDark}; transition: all .25s;
          position: relative;
        }
        .rp-list-item:hover { border-left-color: ${C.red}; background: #fff8f5; transform: translateX(4px); }

        /* CASE HIGHLIGHTS */
        .rp-cards-grid { display:grid; grid-template-columns:1fr 1fr; gap:1.5rem; margin-top:1.5rem; }
        @media(max-width:640px){ .rp-cards-grid { grid-template-columns:1fr; } }
        .rp-card {
          background: ${C.white}; overflow:hidden; cursor:pointer;
          transition: all .3s; border-top: 3px solid transparent;
          box-shadow: 0 2px 16px rgba(0,0,0,0.06);
        }
        .rp-card:hover { border-top-color: ${C.red}; transform: translateY(-4px); box-shadow: 8px 8px 0 rgba(200,155,60,0.15); }
        .rp-card img { width:100%; height:180px; object-fit:cover; display:block; filter:saturate(0.7); transition:.4s; }
        .rp-card:hover img { filter:saturate(1); }
        .rp-card-body { padding: 1.1rem 1.2rem; }
        .rp-card-body h4 { font-family:'Cormorant Garamond',serif; font-size:1.15rem; font-weight:700; color:${C.textDark}; margin:0 0 .4rem; }
        .rp-card-body p { font-size:.85rem; color:${C.textMuted}; line-height:1.6; margin:0; }

        /* ── SIDEBAR ── */
        .rp-sidebar { display:flex; flex-direction:column; gap:2rem; }
        .rp-sidebar-sticky { position:sticky; top:7rem; display:flex; flex-direction:column; gap:2rem; }

        /* FORM CARD */
        .rp-form-card {
          background: ${C.red};
          padding: 2.5rem 2rem;
          position: relative; overflow: hidden;
          box-shadow: 10px 10px 0 rgba(200,155,60,0.2);
        }
        .rp-form-card::before {
          content:''; position:absolute; top:0; left:0; right:0; height:3px;
          background: linear-gradient(to right, ${C.gold}, rgba(200,155,60,0.4));
        }
        .rp-form-card-wm { position:absolute; bottom:-20px; right:-20px; opacity:.05; pointer-events:none; }
        .rp-form-label { font-size:.6rem; letter-spacing:3px; text-transform:uppercase; color:${C.gold}; font-weight:700; margin-bottom:.3rem; display:block; }
        .rp-form-title { font-family:'Cormorant Garamond',serif; font-size:2rem; font-weight:700; color:#fff; margin:0 0 .4rem; }
        .rp-form-sub { font-size:.8rem; color:rgba(255,255,255,0.55); margin:0 0 1.8rem; font-style:italic; }
        .rp-input {
          width:100%; padding:.85rem 1rem;
          background:rgba(255,255,255,0.08); border:1px solid rgba(255,255,255,0.15);
          color:#fff; font-size:.9rem; font-family:'Nunito Sans',sans-serif;
          outline:none; transition:all .25s; margin-bottom:1rem; display:block;
        }
        .rp-input::placeholder { color:rgba(255,255,255,0.4); }
        .rp-input:focus { background:rgba(255,255,255,0.14); border-color:${C.gold}; }
        .rp-input-focused { background:rgba(255,255,255,0.14) !important; border-color:${C.gold} !important; }
        .rp-textarea { resize:vertical; min-height:90px; }
        .rp-submit-btn {
          width:100%; padding:1rem; background:${C.gold}; color:${C.textDark};
          font-weight:700; font-size:.7rem; letter-spacing:4px; text-transform:uppercase;
          border:none; cursor:pointer; display:flex; align-items:center; justify-content:center; gap:.6rem;
          transition:all .3s; margin-top:.5rem;
        }
        .rp-submit-btn:hover { background:${C.goldLight}; transform:translateY(-2px); box-shadow:0 6px 20px rgba(200,155,60,0.4); }
        .rp-success { background:rgba(200,155,60,0.15); border:1px solid ${C.gold}; padding:.9rem 1rem; color:${C.gold}; font-size:.8rem; text-align:center; margin-top:1rem; letter-spacing:1px; }

        /* CONTACT CARD */
        .rp-contact-card { background:${C.white}; padding:1.8rem; border:1px solid rgba(0,0,0,0.08); box-shadow:4px 4px 0 rgba(200,155,60,0.12); }
        .rp-contact-row { display:flex; align-items:center; gap:.8rem; font-size:.85rem; color:${C.textMuted}; padding:.6rem 0; border-bottom:1px solid rgba(0,0,0,0.05); }
        .rp-contact-row:last-child { border-bottom:none; }
        .rp-contact-icon { width:32px; height:32px; background:${C.red}; display:flex; align-items:center; justify-content:center; flex-shrink:0; }

        /* POPUP */
        .rp-popup-overlay { position:fixed; inset:0; background:rgba(10,5,3,0.75); display:flex; align-items:center; justify-content:center; z-index:999; backdrop-filter:blur(4px); }
        .rp-popup { background:${C.white}; max-width:480px; width:90%; overflow:hidden; position:relative; box-shadow:20px 20px 60px rgba(0,0,0,0.3); border-top:4px solid ${C.gold}; }
        .rp-popup img { width:100%; height:240px; object-fit:cover; }
        .rp-popup-body { padding:1.8rem; }
        .rp-popup-body h2 { font-family:'Cormorant Garamond',serif; font-size:1.6rem; font-weight:700; margin:0 0 .6rem; color:${C.textDark}; }
        .rp-popup-body p { font-size:.9rem; color:${C.textMuted}; line-height:1.7; margin:0; }
        .rp-popup-close { position:absolute; top:12px; right:12px; background:${C.red}; color:#fff; border:none; width:32px; height:32px; cursor:pointer; font-size:1rem; display:flex; align-items:center; justify-content:center; }
      `}
      </style>

      <Header />

      <div className="rp-body">
        {/* ── HERO ─────────────────────────────────────────────── */}
        <div className="rp-hero">
          {service.bannerImage && (
            <img
              className="rp-hero-bg"
              src={service.bannerImage}
              alt={service.title}
            />
          )}
          <div className="rp-hero-overlay" />
          <div className="rp-hero-deco-left" />
          <div className="rp-hero-deco-right" />
          <div className="rp-hero-content">
            <div className="rp-hero-breadcrumb">
              <Link to="/">Home</Link>
              <ChevronRight size={10} />
              <Link to="/services">Services</Link>
              <ChevronRight size={10} />
              <span style={{ color: "rgba(255,255,255,0.7)" }}>
                {service.title}
              </span>
            </div>
            <div className="rp-hero-badge">{service.title}</div>
            <h1 style={{ marginTop: "1rem" }}>{service.title}</h1>
          </div>
        </div>

        {/* ── MAIN GRID ─────────────────────────────────────────── */}
        <div className="rp-main">
          {/* LEFT Article */}
          <div>
            {/* Intro */}
            <div style={{ marginBottom: "3.5rem" }}>
              <p className="rp-section-label">Overview</p>
              <h2 className="rp-section-title">About {service.title}</h2>
              {service.fullDesc?.intro && (
                <div className="rp-intro-text">{service.fullDesc.intro}</div>
              )}
            </div>

            {/* Sections */}
            {service.fullDesc?.sections?.map((section, i) => (
              <div key={i} className="rp-section">
                <p className="rp-section-label">
                  Section {String(i + 1).padStart(2, "0")}
                </p>
                <h3
                  className="rp-section-title"
                  style={{ fontSize: "clamp(1.4rem,2.5vw,2rem)" }}
                >
                  {section.title}
                </h3>
                {section.content && (
                  <p className="rp-section-content">{section.content}</p>
                )}
                {section.list && (
                  <div className="rp-list-grid">
                    {section.list.map((item, idx) => (
                      <div key={idx} className="rp-list-item">
                        <span
                          style={{
                            position: "absolute",
                            left: "0.5rem",
                            top: "50%",
                            transform: "translateY(-50%)",
                            width: 6,
                            height: 1,
                            background: C.gold,
                            display: "block",
                          }}
                        />
                        {item}
                      </div>
                    ))}
                  </div>
                )}
                {i < service.fullDesc.sections.length - 1 && (
                  <div className="rp-divider">
                    <Scale size={14} color={C.gold} />
                  </div>
                )}
              </div>
            ))}

            {/* Case Highlights */}
            {service.featureDetails?.length > 0 && (
              <div style={{ marginTop: "4rem" }}>
                <p className="rp-section-label">Portfolio</p>
                <h3 className="rp-section-title">Case Highlights</h3>
                <div className="rp-cards-grid">
                  {service.featureDetails.map((item, i) => (
                    <div
                      key={i}
                      className="rp-card"
                      onClick={() => setSelectedItem(item)}
                    >
                      <img src={item.img} alt={item.name} />
                      <div className="rp-card-body">
                        <h4>{item.name}</h4>
                        <p>{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* RIGHT  Sidebar */}
          <div className="rp-sidebar">
            <div className="rp-sidebar-sticky">
              {/* Consultation Form */}
              <div className="rp-form-card">
                <div className="rp-form-card-wm">
                  <Scale size={180} color="#fff" />
                </div>
                <span className="rp-form-label">No-Cost Initial Meeting</span>
                <h3 className="rp-form-title">Free Consultation</h3>
                <p className="rp-form-sub">
                  "Justice begins with a single conversation."
                </p>

                <form onSubmit={handleSubmit}>
                  {[
                    {
                      name: "name",
                      placeholder: "Your Full Name",
                      type: "text",
                    },
                    {
                      name: "email",
                      placeholder: "Email Address",
                      type: "email",
                    },
                    {
                      name: "phone",
                      placeholder: "Phone (10 digits)",
                      type: "tel",
                    },
                  ].map((f) => (
                    <input
                      key={f.name}
                      className={`rp-input${focusedField === f.name ? " rp-input-focused" : ""}`}
                      type={f.type}
                      name={f.name}
                      placeholder={f.placeholder}
                      value={formData[f.name]}
                      onChange={handleChange}
                      onFocus={() => setFocusedField(f.name)}
                      onBlur={() => setFocusedField(null)}
                      required
                    />
                  ))}
                  <textarea
                    className={`rp-input rp-textarea${focusedField === "message" ? " rp-input-focused" : ""}`}
                    name="message"
                    placeholder="How can we help you?"
                    value={formData.message}
                    onChange={handleChange}
                    onFocus={() => setFocusedField("message")}
                    onBlur={() => setFocusedField(null)}
                  />
                  <button type="submit" className="rp-submit-btn">
                    Send Message <Send size={15} />
                  </button>
                  {success && (
                    <div className="rp-success">
                      ✓ Message sent via WhatsApp!
                    </div>
                  )}
                </form>
              </div>

              {/* Contact Info */}
              <div className="rp-contact-card">
                <p
                  style={{
                    fontSize: ".6rem",
                    letterSpacing: "3px",
                    textTransform: "uppercase",
                    color: C.gold,
                    fontWeight: 700,
                    marginBottom: "1rem",
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                  }}
                >
                  <span
                    style={{
                      display: "inline-block",
                      width: 20,
                      height: 1,
                      background: C.gold,
                    }}
                  />
                  Direct Contact
                </p>
                <div className="rp-contact-row">
                  <div className="rp-contact-icon">
                    <Phone size={14} color="#fff" />
                  </div>
                  <span>+9196619 00233</span>
                </div>
                <div className="rp-contact-row">
                  <div className="rp-contact-icon">
                    <Mail size={14} color="#fff" />
                  </div>
                  <span style={{ wordBreak: "break-all" }}>
                    admin@rplawpartners.com <br /> ritika@rplawpartners.com
                  </span>
                </div>
                <div className="rp-contact-row">
                  <div className="rp-contact-icon">
                    <MapPin size={14} color="#fff" />
                  </div>
                  <span>
                    Chamber No.- 890, Patiala House Court, New Delhi- 110001
                    23/07, Wave One, Sector- 18, Noida, U. P.- 201301
                  </span>
                </div>
              </div>

              {/* Trust badges */}
              <div style={{ display: "flex", gap: "1rem" }}>
                {[
                  {
                    icon: <Shield size={18} color={C.gold} />,
                    label: "Confidential",
                  },
                  {
                    icon: <Award size={18} color={C.gold} />,
                    label: "Experienced",
                  },
                  {
                    icon: <Scale size={18} color={C.gold} />,
                    label: "Trusted",
                  },
                ].map((b, i) => (
                  <div
                    key={i}
                    style={{
                      flex: 1,
                      background: C.white,
                      border: "1px solid rgba(0,0,0,0.07)",
                      padding: "1rem .5rem",
                      textAlign: "center",
                      borderTop: `2px solid ${C.gold}`,
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "center",
                        marginBottom: 6,
                      }}
                    >
                      {b.icon}
                    </div>
                    <p
                      style={{
                        fontSize: ".6rem",
                        letterSpacing: "2px",
                        textTransform: "uppercase",
                        color: C.textMuted,
                        margin: 0,
                        fontWeight: 700,
                      }}
                    >
                      {b.label}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── POPUP ──────────────────────────────────────────────── */}
      {selectedItem && (
        <div className="rp-popup-overlay" onClick={() => setSelectedItem(null)}>
          <div className="rp-popup" onClick={(e) => e.stopPropagation()}>
            <button
              className="rp-popup-close"
              onClick={() => setSelectedItem(null)}
            >
              <X size={16} />
            </button>
            <img src={selectedItem.img} alt={selectedItem.name} />
            <div className="rp-popup-body">
              <h2>{selectedItem.name}</h2>
              <p>{selectedItem.desc}</p>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </>
  );
};

export default ServicesDetail;
